import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { RuleTypeDetailsProps } from "@redotech/redo-return-ui/flows/rules/multiple-actions";
import { TopicState } from "@redotech/redo-return-ui/flows/rules/topic";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import * as conditionsCss from "./conditions.module.css";

export const TopicDetails = memo(function TopicDetails({
  state,
  setState,
  disabled,
}: RuleTypeDetailsProps<TopicState>) {
  const { t } = useTranslation();
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const topicsLoad = useLoad(
    () => supportRpcClient.getConversationTopics({}),
    [supportRpcClient],
  );

  const topics = useMemo(
    () => topicsLoad.value?.topics ?? [],
    [topicsLoad.value],
  );

  const topicItems = useMemo(() => {
    return topics.map((topic) => ({
      id: topic,
      type: "text" as const,
      text: topic,
      value: topic,
    }));
  }, [topics]);

  const selectedItems = useMemo(() => {
    return state.map((topic) => ({
      id: topic,
      type: "text" as const,
      text: topic,
      value: topic,
    }));
  }, [state]);

  const firstSelectedTopic = state[0];
  const displayText =
    firstSelectedTopic === undefined
      ? t("Select topics")
      : state.length === 1
        ? firstSelectedTopic
        : t("{{count}} topics selected", { count: state.length });

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text>{t("Topic")}</Text>
      <RedoFilterDropdownAnchor
        className={conditionsCss.fullWidthButton}
        color={disabled ? "secondary" : "primary"}
        onClick={() => {
          if (!disabled) {
            setDropdownOpen(!dropdownOpen);
          }
        }}
        ref={setDropdownButtonRef}
        text={displayText}
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={topicItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          setState(items.map((item) => item.value));
        }}
        size="xs"
      />
    </Flex>
  );
});
